/**
 * AdminTradingOffers
 * ------------------
 * Master-admin view of every property offer, grouped by property. Admin can
 * override an offer's status to any value from the Status select. Buying
 * position, timeline and notes are shown so the platform team has full
 * context. No emails are sent from admin overrides.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, HandCoins, Search, ExternalLink, Trash2 } from "lucide-react";

const API = getApiUrl();
const STATUSES = ["sent", "accepted", "rejected", "withdrawn"];

function currencySymbol(code) {
  return ({ GBP: "£", USD: "$", EUR: "€" }[code] || code || "");
}

function formatAmount(amount, currency = "£") {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  const sym = currencySymbol(currency) || "£";
  return `${sym}${n.toLocaleString(undefined, {
    maximumFractionDigits: n >= 1000 ? 0 : 2,
  })}`;
}

function StatusBadge({ status }) {
  const cls =
    status === "accepted"
      ? "bg-green-100 text-green-700 border-green-200"
      : status === "rejected"
      ? "bg-red-100 text-red-700 border-red-200"
      : status === "withdrawn"
      ? "bg-slate-100 text-slate-600 border-slate-200"
      : "bg-amber-100 text-amber-700 border-amber-200";
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded border capitalize ${cls}`}
      data-testid={`admin-offer-status-${status || "sent"}`}
    >
      {status || "sent"}
    </span>
  );
}

function OfferCard({ offer, onStatusChange, onReassign, onDelete, properties, busy }) {
  const [note, setNote] = useState("");
  const [nextStatus, setNextStatus] = useState(offer.status || "sent");
  const [reassignId, setReassignId] = useState("");
  const current = offer.status || "sent";
  const dirty = nextStatus !== current || note.trim().length > 0;
  return (
    <div
      className="border-t border-border p-3 space-y-2"
      data-testid={`admin-offer-row-${offer.offer_id}`}
    >
      <div className="flex flex-wrap items-start gap-2 justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">
              {formatAmount(offer.amount, offer.currency)}
            </span>
            <StatusBadge status={current} />
            <span className="text-[11px] text-muted-foreground">
              {offer.created_at
                ? new Date(offer.created_at).toLocaleString()
                : ""}
            </span>
          </div>
          <div className="mt-1 text-xs text-foreground/80">
            <span className="font-medium">
              {offer.buyer?.name || offer.buyer_name || "Buyer"}
            </span>
            {offer.buyer?.email || offer.buyer_email ? (
              <span className="ml-2 text-muted-foreground">
                {offer.buyer?.email || offer.buyer_email}
              </span>
            ) : null}
            {offer.buyer?.phone || offer.buyer_phone ? (
              <span className="ml-2 text-muted-foreground">
                · {offer.buyer?.phone || offer.buyer_phone}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      {(offer.buying_position || offer.move_timeline || offer.position_notes) && (
        <div
          className="border border-border/60 bg-white p-2 text-xs space-y-1"
          data-testid={`admin-offer-position-${offer.offer_id}`}
        >
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
            Buyer position
          </div>
          <div className="flex flex-wrap gap-2">
            {offer.buying_position && (
              <span className="px-2 py-0.5 bg-green-50 text-green-800 border border-green-200 rounded font-medium">
                {offer.buying_position}
              </span>
            )}
            {offer.move_timeline && (
              <span className="px-2 py-0.5 bg-blue-50 text-blue-800 border border-blue-200 rounded">
                Wants to move: {offer.move_timeline}
              </span>
            )}
          </div>
          {offer.position_notes && (
            <div className="text-muted-foreground whitespace-pre-wrap pt-1 border-t border-border/40">
              {offer.position_notes}
            </div>
          )}
        </div>
      )}
      {offer.message ? (
        <div className="text-xs text-muted-foreground whitespace-pre-wrap bg-muted/30 border-l-2 border-border pl-2 py-1">
          “{offer.message}”
        </div>
      ) : null}
      {offer.decision_note ? (
        <div className="text-xs bg-slate-50 border-l-2 border-slate-400 pl-2 py-1 whitespace-pre-wrap">
          <span className="text-slate-500 font-medium">Owner note: </span>
          {offer.decision_note}
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-2 pt-1 border-t border-border/50 mt-1">
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Set status
          </Label>
          <Select value={nextStatus} onValueChange={setNextStatus}>
            <SelectTrigger
              className="rounded-none mt-1 h-8 text-xs min-w-[140px]"
              data-testid={`admin-offer-status-select-${offer.offer_id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem
                  key={s}
                  value={s}
                  data-testid={`admin-offer-status-option-${offer.offer_id}-${s}`}
                >
                  <span className="capitalize">{s}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Note (optional)
          </Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-none mt-1 h-8 text-xs"
            placeholder="Admin override reason"
            data-testid={`admin-offer-note-input-${offer.offer_id}`}
          />
        </div>
        <Button
          size="sm"
          className="rounded-none h-8 text-xs"
          disabled={!dirty || busy}
          onClick={() => onStatusChange(offer, nextStatus, note).then(() => setNote(""))}
          data-testid={`admin-offer-save-status-${offer.offer_id}`}
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2 pt-1 border-t border-border/50">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Reassign to property
          </Label>
          <Select value={reassignId} onValueChange={setReassignId}>
            <SelectTrigger
              className="rounded-none mt-1 h-8 text-xs"
              data-testid={`admin-offer-reassign-select-${offer.offer_id}`}
            >
              <SelectValue placeholder="Pick a different property…" />
            </SelectTrigger>
            <SelectContent>
              {properties
                .filter((p) => p.property_id !== offer.property_id)
                .map((p) => (
                  <SelectItem
                    key={p.property_id}
                    value={p.property_id}
                    data-testid={`admin-offer-reassign-option-${offer.offer_id}-${p.property_id}`}
                  >
                    <span className="text-xs">
                      {p.title || p.display_address || p.property_id}
                    </span>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="rounded-none h-8 text-xs"
          disabled={!reassignId || busy}
          onClick={() => onReassign(offer, reassignId).then(() => setReassignId(""))}
          data-testid={`admin-offer-reassign-apply-${offer.offer_id}`}
        >
          Move
        </Button>
        <Button
          size="sm"
          variant="destructive"
          className="rounded-none h-8 text-xs ml-auto"
          disabled={busy}
          onClick={() => onDelete(offer)}
          data-testid={`admin-offer-delete-${offer.offer_id}`}
          title="Delete this offer permanently"
        >
          <Trash2 className="h-3 w-3 mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
}

export default function AdminTradingOffers() {
  const [offers, setOffers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [offersRes, propsRes] = await Promise.all([
        axios.get(`${API}/admin/offers`, { withCredentials: true }),
        axios.get(`${API}/admin/properties`, { withCredentials: true }),
      ]);
      setOffers(offersRes.data?.offers || []);
      // /admin/properties returns a bare array (not wrapped in {properties: []})
      const propsData = propsRes.data;
      setProperties(Array.isArray(propsData) ? propsData : (propsData?.properties || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load offers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = async (offer, status, note) => {
    setBusy(offer.offer_id);
    try {
      await axios.put(
        `${API}/admin/offers/${offer.offer_id}/status`,
        { status, note },
        { withCredentials: true }
      );
      toast.success(`Offer marked ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update offer");
    } finally {
      setBusy(null);
    }
  };

  const reassignProperty = async (offer, property_id) => {
    setBusy(offer.offer_id);
    try {
      await axios.put(
        `${API}/admin/offers/${offer.offer_id}/property`,
        { property_id },
        { withCredentials: true }
      );
      toast.success("Offer moved to new property");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reassign offer");
    } finally {
      setBusy(null);
    }
  };

  const deleteOffer = async (offer) => {
    if (!window.confirm(`Delete this offer of ${formatAmount(offer.amount, offer.currency)}? This cannot be undone.`)) return;
    setBusy(offer.offer_id);
    try {
      await axios.delete(`${API}/admin/offers/${offer.offer_id}`, { withCredentials: true });
      toast.success("Offer deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete offer");
    } finally {
      setBusy(null);
    }
  };

  const [purgingTest, setPurgingTest] = useState(false);
  const deleteTestBuyerOffers = async () => {
    try {
      setPurgingTest(true);
      // Preview first so we can show the real count in the confirm dialog.
      const { data } = await axios.get(
        `${API}/admin/offers/test-buyers/preview`,
        { withCredentials: true }
      );
      const n = data?.count || 0;
      if (n === 0) {
        toast.info("No test-buyer offers found");
        return;
      }
      if (!window.confirm(
        `Delete ${n} offer${n === 1 ? "" : "s"} where the buyer name or email contains "test"? This cannot be undone.`
      )) return;
      const res = await axios.delete(
        `${API}/admin/offers/test-buyers`,
        { withCredentials: true }
      );
      toast.success(`Deleted ${res.data?.deleted ?? n} test-buyer offer${(res.data?.deleted ?? n) === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to purge test offers");
    } finally {
      setPurgingTest(false);
    }
  };

  const propertyGroups = useMemo(() => {
    const s = search.trim().toLowerCase();
    const filtered = offers.filter((o) => {
      if (statusFilter !== "all" && (o.status || "sent") !== statusFilter) return false;
      if (!s) return true;
      const hay = [
        o.property?.title,
        o.property?.display_address,
        o.property?.postcode,
        o.buyer?.name,
        o.buyer?.email,
        o.buyer_name,
        o.buyer_email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(s);
    });
    const map = new Map();
    for (const o of filtered) {
      const key = o.property_id;
      if (!map.has(key)) {
        map.set(key, {
          property: o.property || { property_id: key, title: "Unknown property" },
          offers: [],
        });
      }
      map.get(key).offers.push(o);
    }
    return Array.from(map.values());
  }, [offers, search, statusFilter]);

  const totalOpen = offers.filter((o) => (o.status || "sent") === "sent").length;

  return (
    <div className="bg-white border border-border" data-testid="admin-trading-offers">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <HandCoins className="h-5 w-5" /> Offers
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Every offer across the platform, grouped by property. Admin overrides
          set the offer status without emailing the parties.
        </p>
        <div className="flex flex-wrap items-end gap-3 mt-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search property, buyer name, email, postcode…"
              className="rounded-none pl-8"
              data-testid="admin-trading-offers-search"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Status
            </Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                className="rounded-none mt-1 min-w-[140px]"
                data-testid="admin-trading-offers-status-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({offers.length})</SelectItem>
                <SelectItem value="sent">Open ({totalOpen})</SelectItem>
                {STATUSES.filter((s) => s !== "sent").map((s) => (
                  <SelectItem key={s} value={s}>
                    <span className="capitalize">{s}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={load}
            disabled={loading}
            data-testid="admin-trading-offers-refresh"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
          </Button>
          <Button
            variant="destructive"
            className="rounded-none"
            onClick={deleteTestBuyerOffers}
            disabled={purgingTest || loading}
            data-testid="admin-trading-offers-purge-test"
            title="Delete every offer whose buyer name or email contains 'test'"
          >
            {purgingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1" /> Delete test offers</>}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : propertyGroups.length === 0 ? (
        <div
          className="text-sm text-muted-foreground text-center py-12"
          data-testid="admin-trading-offers-empty"
        >
          No offers match the current filter.
        </div>
      ) : (
        <ul className="divide-y divide-border" data-testid="admin-trading-offers-list">
          {propertyGroups.map(({ property: p, offers: propOffers }) => (
            <li
              key={p.property_id}
              className="border-l-4 border-transparent hover:border-primary/50 transition-colors"
              data-testid={`admin-offer-property-${p.property_id}`}
            >
              <div className="flex gap-4 p-4 bg-muted/10">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <a
                      href={`/property/${p.property_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-serif text-base font-medium hover:underline inline-flex items-center gap-1"
                    >
                      {p.title || "Property"}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    <span className="text-[11px] text-muted-foreground">
                      {propOffers.length} offer{propOffers.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  {p.display_address && (
                    <div className="text-xs text-muted-foreground truncate">
                      {p.display_address}
                      {p.postcode ? ` · ${p.postcode}` : ""}
                    </div>
                  )}
                  {p.price ? (
                    <div className="text-sm mt-0.5">
                      Listed at{" "}
                      <span className="font-semibold">
                        {formatAmount(p.price, p.currency)}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
              {propOffers.map((o) => (
                <OfferCard
                  key={o.offer_id}
                  offer={o}
                  onStatusChange={changeStatus}
                  onReassign={reassignProperty}
                  onDelete={deleteOffer}
                  properties={properties}
                  busy={busy === o.offer_id}
                />
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
